'use client';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import React from 'react';
import { Citation } from '@osmosis/shared/types/learn';
import { Contributor } from '@osmosis/shared/types/learn';

import { FormattedMessage } from 'react-intl';

type Props = {
  citations: Citation;
};

type ListProps = {
  contributors: Contributor[];
  dataTestid: string;
};

const uniqContributorBy = (
  // eslint-disable-next-line default-param-last
  contributors: Contributor[] = [],
  contributorKey: keyof Contributor,
): Contributor[] => {
  const seen = new Set<Contributor[keyof Contributor]>();

  return contributors.filter((contributor) => {
    const value = contributor[contributorKey];
    if (seen.has(value)) return false;
    seen.add(value);

    return true;
  });
};

/** . */
const ContributorList = (props: ListProps) => {
  const { contributors, dataTestid } = props;

  return (
    <Flex className={`flex-wrap`}>
      {contributors?.map((contributor: Contributor, index: number) => (
        <a
          data-testid={`${dataTestid}-text-${contributor.id}`}
          href={
            contributor.link ? `/our-team/${contributor.link}` : '/our-team'
          }
          target="_blank"
          key={contributor.id}
          className={`text-primary-500 text-sm mr-1 cursor-pointer no-underline`}
        >
          {index < contributors.length - 1
            ? // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              `${contributor.name},`
            : contributor.name}
        </a>
      ))}
    </Flex>
  );
};

/**
 * The contributors.
 */
const Contributors = (props: Props) => {
  const { citations } = props;
  const { reviewer, ...contributorsObj } = citations;

  // Get unique name entries
  const reviewers = uniqContributorBy(reviewer, 'name');
  const contributors = uniqContributorBy(
    Object.values(contributorsObj).reduce(
      (arr, current) => [...current, ...arr],
      [],
    ),
    'name',
  );

  return (
    <Flex data-testid="contributors-wrapper" className={`flex-col gap-4`}>
      <Box data-testid="reviewers-block-wrapper" className={`mb-1`}>
        <H4 data-testid="reviewers-block-title">
          <FormattedMessage
            id="learn.transcript.content_reviewers"
            defaultMessage="Content Reviewers"
          />
        </H4>
        <ContributorList contributors={reviewers} dataTestid="reviewers" />
      </Box>

      <Box data-testid="contributors-block-wrapper">
        <H4 data-testid="contributors-block-title" className={`mb-1`}>
          <FormattedMessage
            id="learn.transcript.contributors"
            defaultMessage="Contributors"
          />
        </H4>
        <ContributorList
          contributors={contributors}
          dataTestid="contributors"
        />
      </Box>
    </Flex>
  );
};

export default Contributors;
